// UPost - Affiliate Media Kit
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Layout } from '../components/Layout';
import { 
  Copy, Download, CheckCircle, ArrowLeft, Image, Mail, 
  MessageCircle, Youtube, Facebook, Instagram, Linkedin
} from 'lucide-react';

export function MediaKit() {
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [copied, setCopied] = useState('');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadReferralCode();
  }, []);

  async function loadReferralCode() {
    try {
      const data = await api.request('/referrals/my-code');
      setReferralCode(data.referral_code);
      setReferralLink(data.referral_link);
    } catch (err) {
      console.error('Failed to load referral code:', err);
    }
  }

  function copyToClipboard(text, label) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  }

  // Social Media Templates
  const templates = {
    twitter: `🚀 Just discovered @UPost - an amazing AI-powered writing platform for novelists & screenwriters!

✍️ Unlimited AI assistance
📝 Built-in copyright protection
💾 Auto-save & version history
📄 Export to Word/PDF

Try it free: ${referralLink}

#WritingCommunity #AmWriting #Screenwriting`,

    facebook: `📚 Fellow Writers! I've been using UPost for my novel/screenplay writing and it's a game-changer!

What makes it special:
✅ AI-powered writing assistant (like having a co-author!)
✅ Built-in copyright protection with timestamps
✅ Clean, distraction-free editor
✅ Export to Word & PDF
✅ Free tier with 18,400 AI words/month

Perfect for:
📖 Novelists expanding their manuscripts
🎬 Screenwriters crafting scripts
✏️ Content creators needing AI help

Check it out (my referral link - we both get bonuses!):
${referralLink}

#WritingLife #NovelWriting #Screenwriting`,

    instagram: `✨ New writing tool alert! ✨

I've been testing UPost - an AI writing platform built specifically for creative writers 📚

What I love:
• AI writing assistant that actually understands story structure
• Copyright protection built-in
• Beautiful, distraction-free interface
• Free to start!

Link in bio or DM me for my referral link!

#WritingCommunity #AmWriting #WriterLife #NovelWriting #Screenwriting #WritingTools #AIWriting #CreativeWriting`,

    linkedin: `Excited to share a tool that's been transforming my writing workflow! 📝

UPost is an AI-powered writing platform designed specifically for creative writers - novelists, screenwriters, and content creators.

Key features that stand out:
• Advanced AI writing assistance (Claude 3.5 integration)
• Built-in copyright protection with blockchain timestamps
• Version history and auto-save
• Professional export options (Word, PDF)
• Generous free tier (18,400 AI words/month)

What I appreciate most is how it balances AI assistance with preserving your unique voice. It's like having an intelligent editor available 24/7.

Perfect for:
- Authors working on manuscripts
- Screenwriters developing scripts
- Content creators needing AI support

Try it free: ${referralLink}

#WritingTools #CreativeWriting #AI #ContentCreation`,

    youtube: `🎬 VIDEO DESCRIPTION TEMPLATE

In this video, I'm sharing my experience with UPost - an AI-powered writing platform that's been a game-changer for my creative writing projects.

⏰ TIMESTAMPS:
0:00 - Introduction
0:30 - What is UPost?
1:15 - AI Writing Assistant Demo
3:00 - Copyright Protection Feature
4:20 - Export Options
5:00 - Pricing & Free Tier
6:00 - My Final Thoughts

🔗 LINKS:
Try UPost Free: ${referralLink}
(Using my link gets you bonus AI words!)

💡 WHAT IS UPOST?
UPost is an AI writing platform built for novelists, screenwriters, and creative writers. It combines powerful AI assistance with essential features like copyright protection and professional export options.

✨ KEY FEATURES:
✅ AI-powered writing assistant
✅ Built-in copyright protection
✅ Clean, distraction-free editor
✅ Version history & auto-save
✅ Export to Word & PDF
✅ Free tier: 18,400 AI words/month
✅ Pro tier: Unlimited AI words ($19/mo)

📝 WHO IS IT FOR?
- Novelists expanding manuscripts
- Screenwriters crafting scripts
- Content creators needing AI help
- Anyone writing long-form content

💰 PRICING:
Free: 18,400 AI words/month
Pro: $19/month (unlimited AI words)

📧 CONTACT:
Questions? Leave a comment below!

#UPost #AIWriting #WritingTools #NovelWriting #Screenwriting #ContentCreation

*Disclaimer: This is my referral link. If you sign up, we both get bonuses!`,

    email: `Subject: Check out this AI writing tool I've been using 📝

Hey [Name],

I wanted to share something that's been really helpful for my writing lately.

I've been using UPost - an AI-powered writing platform built specifically for creative writers (novelists, screenwriters, content creators).

What makes it different:
• AI assistant that actually understands story structure
• Built-in copyright protection (automatic timestamps)
• Clean interface without distractions
• Free to start with a generous word limit

I think you'd find it useful for [your novel/screenplay/project]. 

Here's my referral link if you want to check it out:
${referralLink}

(Full disclosure: we both get bonus AI words if you sign up through my link!)

Let me know what you think if you try it.

Best,
[Your Name]`,

    blog: `# UPost Review: AI Writing Platform for Creative Writers

## What is UPost?

UPost is an AI-powered writing platform designed specifically for novelists, screenwriters, and creative writers. After using it for [X weeks/months], I wanted to share my experience.

## Key Features

### 1. AI Writing Assistant
Powered by Claude 3.5, the AI understands context and story structure. It's like having a co-author who helps with writer's block, dialogue, and scene expansion.

### 2. Copyright Protection
Built-in blockchain-based timestamps protect your work. Every save creates a verifiable timestamp.

### 3. Clean Interface
Distraction-free editor that lets you focus on writing. No clutter, just you and your words.

### 4. Export Options
Export to Word or PDF with proper formatting. Great for sharing with agents or publishers.

## Pricing

**Free Tier:** 18,400 AI words/month (perfect for trying it out)
**Pro Tier:** $19/month for unlimited AI words

## My Experience

[Share your personal experience, specific use case, results]

## Who Should Use It?

- Novelists working on manuscripts
- Screenwriters developing scripts
- Content creators needing AI assistance
- Anyone writing long-form content

## Get Started

Try UPost free: ${referralLink}

*Disclosure: This is my referral link. We both get bonuses if you sign up!*

---

Questions? Leave a comment below!`
  };

  // Banner sizes for different platforms
  const banners = [
    { name: 'Facebook Cover', size: '820x312', desc: 'Perfect for Facebook page covers' },
    { name: 'Twitter Header', size: '1500x500', desc: 'Twitter/X profile banner' },
    { name: 'LinkedIn Banner', size: '1584x396', desc: 'LinkedIn company page' },
    { name: 'YouTube Thumbnail', size: '1280x720', desc: 'Video thumbnails' },
    { name: 'Instagram Post', size: '1080x1080', desc: 'Square format post' },
    { name: 'Instagram Story', size: '1080x1920', desc: 'Vertical story format' },
    { name: 'Blog Banner', size: '1200x628', desc: 'Blog post featured image' },
    { name: 'Email Header', size: '600x200', desc: 'Email newsletter header' }
  ];

  return (
    <Layout showFooter={false}>
      <div className="min-h-screen bg-gray-50">
        {/* Page Header */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Affiliate Media Kit</h1>
                <p className="text-sm text-gray-600">Marketing resources to promote UPost</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">{user?.email}</span>
                <Link to="/referrals">
                  <Button variant="ghost" size="sm">
                    <ArrowLeft size={16} className="mr-2" />
                    Back to Referrals
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

      <div className="container mx-auto px-4 py-8">
        {/* Hero */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">
            Affiliate Media Kit
          </h1>
          <p className="text-gray-600 text-lg">
            Ready-to-use promotional materials for all platforms
          </p>
        </div>

        {/* Your Referral Info */}
        <Card className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Referral Code
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-2 font-mono font-bold text-purple-600">
                    {referralCode}
                  </div>
                  <Button
                    onClick={() => copyToClipboard(referralCode, 'code')}
                    variant="outline"
                    size="sm"
                  >
                    {copied === 'code' ? <CheckCircle size={16} /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Referral Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={referralLink}
                    readOnly
                    className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm"
                  />
                  <Button
                    onClick={() => copyToClipboard(referralLink, 'link')}
                    variant="outline"
                    size="sm"
                  >
                    {copied === 'link' ? <CheckCircle size={16} /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Social Media Templates */}
        <h2 className="text-2xl font-bold mb-4">Social Media Templates</h2>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Twitter */}
          <Card>
            <CardHeader className="bg-blue-50">
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="text-blue-500" size={24} />
                Twitter / X
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-64 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-sans text-gray-700">
                  {templates.twitter}
                </pre>
              </div>
              <Button
                onClick={() => copyToClipboard(templates.twitter, 'twitter')}
                className="w-full"
                variant="outline"
              >
                {copied === 'twitter' ? (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-2" />
                    Copy Tweet Template
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Facebook */}
          <Card>
            <CardHeader className="bg-blue-50">
              <CardTitle className="flex items-center gap-2">
                <Facebook className="text-blue-600" size={24} />
                Facebook
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-64 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-sans text-gray-700">
                  {templates.facebook}
                </pre>
              </div>
              <Button
                onClick={() => copyToClipboard(templates.facebook, 'facebook')}
                className="w-full"
                variant="outline"
              >
                {copied === 'facebook' ? (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-2" />
                    Copy Facebook Post
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Instagram */}
          <Card>
            <CardHeader className="bg-pink-50">
              <CardTitle className="flex items-center gap-2">
                <Instagram className="text-pink-600" size={24} />
                Instagram
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-64 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-sans text-gray-700">
                  {templates.instagram}
                </pre>
              </div>
              <Button
                onClick={() => copyToClipboard(templates.instagram, 'instagram')}
                className="w-full"
                variant="outline"
              >
                {copied === 'instagram' ? (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-2" />
                    Copy Instagram Caption
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* LinkedIn */}
          <Card>
            <CardHeader className="bg-blue-50">
              <CardTitle className="flex items-center gap-2">
                <Linkedin className="text-blue-700" size={24} />
                LinkedIn
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-64 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-sans text-gray-700">
                  {templates.linkedin}
                </pre>
              </div>
              <Button
                onClick={() => copyToClipboard(templates.linkedin, 'linkedin')}
                className="w-full"
                variant="outline"
              >
                {copied === 'linkedin' ? (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-2" />
                    Copy LinkedIn Post
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* YouTube */}
          <Card className="md:col-span-2">
            <CardHeader className="bg-red-50">
              <CardTitle className="flex items-center gap-2">
                <Youtube className="text-red-600" size={24} />
                YouTube Video Description
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-96 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-sans text-gray-700">
                  {templates.youtube}
                </pre>
              </div>
              <Button
                onClick={() => copyToClipboard(templates.youtube, 'youtube')}
                className="w-full"
                variant="outline"
              >
                {copied === 'youtube' ? (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-2" />
                    Copy YouTube Description
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Email */}
          <Card className="md:col-span-2">
            <CardHeader className="bg-gray-50">
              <CardTitle className="flex items-center gap-2">
                <Mail className="text-gray-700" size={24} />
                Email Template
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-64 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-sans text-gray-700">
                  {templates.email}
                </pre>
              </div>
              <Button
                onClick={() => copyToClipboard(templates.email, 'email')}
                className="w-full"
                variant="outline"
              >
                {copied === 'email' ? (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-2" />
                    Copy Email Template
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Blog Post */}
          <Card className="md:col-span-2">
            <CardHeader className="bg-green-50">
              <CardTitle className="flex items-center gap-2">
                <Image className="text-green-600" size={24} />
                Blog Post Template
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-96 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap font-sans text-gray-700">
                  {templates.blog}
                </pre>
              </div>
              <Button
                onClick={() => copyToClipboard(templates.blog, 'blog')}
                className="w-full"
                variant="outline"
              >
                {copied === 'blog' ? (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-2" />
                    Copy Blog Template
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Banner Sizes Reference */}
        <h2 className="text-2xl font-bold mb-4">Recommended Banner Sizes</h2>
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              {banners.map((banner, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Image className="text-purple-600 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-semibold text-gray-900">{banner.name}</p>
                    <p className="text-sm text-purple-600">{banner.size}px</p>
                    <p className="text-xs text-gray-600">{banner.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>💡 Tip:</strong> Use Canva, Figma, or Adobe Spark to create custom banners 
                with these dimensions. Include your referral code or link in the design!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Brand Guidelines */}
        <h2 className="text-2xl font-bold mb-4">Brand Guidelines</h2>
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h3 className="font-bold mb-2">Brand Colors</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-purple-600"></div>
                    <span className="text-sm">#7c3aed (Purple)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-blue-600"></div>
                    <span className="text-sm">#2563eb (Blue)</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-bold mb-2">Key Messages</h3>
                <ul className="text-sm space-y-1 text-gray-700">
                  <li>• AI-powered writing platform</li>
                  <li>• Built for creative writers</li>
                  <li>• Copyright protection included</li>
                  <li>• Free tier available</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold mb-2">Hashtags</h3>
                <p className="text-sm text-gray-700">
                  #WritingCommunity #AmWriting #WriterLife #NovelWriting 
                  #Screenwriting #AIWriting #WritingTools
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card>
          <CardHeader>
            <CardTitle>Promotion Tips</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-700">
              <p>✅ <strong>Be authentic:</strong> Share your genuine experience with UPost</p>
              <p>✅ <strong>Use visuals:</strong> Screenshots of your work increase engagement</p>
              <p>✅ <strong>Disclose:</strong> Always mention it's a referral link (builds trust!)</p>
              <p>✅ <strong>Target your audience:</strong> Focus on writers, authors, screenwriters</p>
              <p>✅ <strong>Share benefits:</strong> Highlight how it solves specific problems</p>
              <p>✅ <strong>Be consistent:</strong> Regular posts perform better than one-time promotions</p>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </Layout>
  );
}
